'use client';

import { resolveCmsCarouselSlideLabel } from '@/cms/components/blocks/shared/cms-carousel-label';
import { CmsImage } from '@/cms/components/blocks/shared/cms-image';
import { cn } from '@/lib/ui/utils';
import type { CSSProperties, TouchEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

type MediaIoReviewSlide = {
  author: string;
  role: string;
  quote: string;
  rating: number;
  imageSrc: string;
  imageAlt: string;
  imageWidth?: number;
  imageHeight?: number;
};

type MediaIoReviewCarouselClientProps = {
  blockId: string;
  ariaLabel: string;
  paginationLabel: string;
  slideButtonLabelTemplate?: string | null;
  slides: MediaIoReviewSlide[];
};

const AUTOPLAY_DELAY_MS = 5000;
const MOBILE_REVIEW_MEDIA_QUERY = '(max-width: 48rem)';
const MOBILE_REVIEW_PORTRAIT_WIDTHS = [80, 160, 240];
const SWIPE_DISTANCE_THRESHOLD_PX = 48;

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(query);
    const updateMatches = () => setMatches(mediaQuery.matches);

    updateMatches();
    mediaQuery.addEventListener('change', updateMatches);

    return () => mediaQuery.removeEventListener('change', updateMatches);
  }, [query]);

  return matches;
}

function RatingStars({ rating }: { rating: number }) {
  return (
    <div
      className="cms-block-user-reviews-user-reviews-3-user-reviews-3__stars"
      aria-label={`${rating} out of 5 stars`}
      role="img"
    >
      {Array.from({ length: 5 }, (_, index) => {
        const fill = Math.max(0, Math.min(1, rating - index));

        return (
          <span
            className="cms-block-user-reviews-user-reviews-3-user-reviews-3__starShell"
            key={index}
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              className="cms-block-user-reviews-user-reviews-3-user-reviews-3__starBase"
              focusable="false"
            >
              <path d="m12 2 2.9 6.3 6.9.8-5.1 4.7 1.3 6.8-6-3.4-6 3.4 1.3-6.8-5.1-4.7 6.9-.8L12 2Z" />
            </svg>
            <span
              className="cms-block-user-reviews-user-reviews-3-user-reviews-3__starFillClip"
              style={{ '--star-fill': `${fill * 100}%` } as CSSProperties}
            >
              <svg
                viewBox="0 0 24 24"
                className="cms-block-user-reviews-user-reviews-3-user-reviews-3__starFill"
                focusable="false"
              >
                <path d="m12 2 2.9 6.3 6.9.8-5.1 4.7 1.3 6.8-6-3.4-6 3.4 1.3-6.8-5.1-4.7 6.9-.8L12 2Z" />
              </svg>
            </span>
          </span>
        );
      })}
    </div>
  );
}

function QuoteMark() {
  return (
    <svg
      className="cms-block-user-reviews-user-reviews-3-user-reviews-3__quoteMark"
      viewBox="0 0 106 69"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M105.928 68.1911L77.8873 0.563477H53.9702L68.8153 68.1911H105.928ZM52.8705 68.1911L24.8298 0.563477H0.91272L16.0327 68.1911H52.8705Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ReviewerHeading({
  author,
  role,
  mobile = false,
}: Pick<MediaIoReviewSlide, 'author' | 'role'> & {
  mobile?: boolean;
}) {
  return (
    <h3 className="cms-block-user-reviews-user-reviews-3-user-reviews-3__author">
      {author}
      {role ? (
        <span className="cms-block-user-reviews-user-reviews-3-user-reviews-3__role">
          {mobile ? ` ${role}` : ` (${role})`}
        </span>
      ) : null}
    </h3>
  );
}

function MobileReviewerIdentity({ slide }: { slide: MediaIoReviewSlide }) {
  return (
    <div className="cms-block-user-reviews-user-reviews-3-user-reviews-3__mobileIdentity">
      <div className="cms-block-user-reviews-user-reviews-3-user-reviews-3__mobilePortraitFrame">
        <CmsImage
          className="cms-block-user-reviews-user-reviews-3-user-reviews-3__mobilePortrait"
          src={slide.imageSrc}
          alt=""
          width={slide.imageWidth}
          height={slide.imageHeight}
          widths={MOBILE_REVIEW_PORTRAIT_WIDTHS}
          sizes="5rem"
          variant="compact"
          draggable={false}
        />
      </div>
      <div className="cms-block-user-reviews-user-reviews-3-user-reviews-3__mobileIdentityCopy">
        <ReviewerHeading
          author={slide.author}
          role={slide.role}
          mobile={true}
        />
        <RatingStars rating={slide.rating} />
      </div>
    </div>
  );
}

export function MediaIoReviewCarouselClient({
  blockId,
  ariaLabel,
  paginationLabel,
  slideButtonLabelTemplate,
  slides,
}: MediaIoReviewCarouselClientProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const touchStartXRef = useRef<number | null>(null);
  const isMobileReview = useMediaQuery(MOBILE_REVIEW_MEDIA_QUERY);
  const slideCount = slides.length;
  const carouselId = useMemo(() => `${blockId}-media-io-reviews`, [blockId]);

  useEffect(() => {
    if (!isAutoPlaying || isMobileReview || slideCount <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % slideCount);
    }, AUTOPLAY_DELAY_MS);

    return () => window.clearInterval(timer);
  }, [isAutoPlaying, isMobileReview, slideCount]);

  if (slideCount === 0) {
    return null;
  }

  const selectSlide = (index: number) => {
    setActiveIndex((index + slideCount) % slideCount);
    setIsAutoPlaying(false);
  };

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    if (!isMobileReview || slideCount <= 1) {
      return;
    }

    touchStartXRef.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLElement>) => {
    const touchStartX = touchStartXRef.current;
    touchStartXRef.current = null;

    if (touchStartX === null || !isMobileReview || slideCount <= 1) {
      return;
    }

    const touchEndX = event.changedTouches[0]?.clientX;
    if (touchEndX === undefined) {
      return;
    }

    const swipeDistance = touchEndX - touchStartX;
    if (Math.abs(swipeDistance) < SWIPE_DISTANCE_THRESHOLD_PX) {
      return;
    }

    selectSlide(activeIndex + (swipeDistance < 0 ? 1 : -1));
  };

  return (
    <section
      id={carouselId}
      className="cms-block-user-reviews-user-reviews-3-user-reviews-3__stage"
      aria-label={ariaLabel}
      aria-roledescription="carousel"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        touchStartXRef.current = null;
      }}
    >
      <div className="cms-block-user-reviews-user-reviews-3-user-reviews-3__copyColumn">
        <div className="cms-block-user-reviews-user-reviews-3-user-reviews-3__copyViewport">
          <div
            className="cms-block-user-reviews-user-reviews-3-user-reviews-3__copyTrack"
            style={
              {
                '--active-review-index': activeIndex,
              } as React.CSSProperties
            }
          >
            {slides.map((slide, index) => (
              <article
                className="cms-block-user-reviews-user-reviews-3-user-reviews-3__slide"
                key={`${blockId}-${slide.author}-${index}`}
                aria-hidden={index !== activeIndex}
              >
                <div className="cms-block-user-reviews-user-reviews-3-user-reviews-3__desktopIdentity">
                  <ReviewerHeading author={slide.author} role={slide.role} />
                  <RatingStars rating={slide.rating} />
                </div>
                <MobileReviewerIdentity slide={slide} />
                <p className="cms-block-user-reviews-user-reviews-3-user-reviews-3__quote">
                  {slide.quote}
                </p>
              </article>
            ))}
          </div>
        </div>

        {slideCount > 1 ? (
          <div
            className="cms-block-user-reviews-user-reviews-3-user-reviews-3__pagination"
            aria-label={paginationLabel}
          >
            {slides.map((slide, index) => (
              <button
                className="cms-block-user-reviews-user-reviews-3-user-reviews-3__bullet"
                key={`${blockId}-bullet-${index}`}
                type="button"
                aria-label={resolveCmsCarouselSlideLabel(
                  slideButtonLabelTemplate,
                  index
                )}
                aria-controls={carouselId}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => {
                  selectSlide(index);
                }}
              />
            ))}
          </div>
        ) : null}
      </div>

      <QuoteMark />

      <div className="cms-block-user-reviews-user-reviews-3-user-reviews-3__peopleViewport">
        {slides.map((slide, index) => (
          <CmsImage
            className={cn(
              'cms-block-user-reviews-user-reviews-3-user-reviews-3__person',
              index === activeIndex && 'personActive'
            )}
            src={slide.imageSrc}
            alt={index === activeIndex ? slide.imageAlt : ''}
            width={slide.imageWidth}
            height={slide.imageHeight}
            key={`${blockId}-person-${index}`}
            draggable={false}
          />
        ))}
      </div>
    </section>
  );
}
